import { branchFeatureFlags } from "../../../../config/branchFeatureFlags";
import {
  fetchHiddenSidebarMenus,
  useHiddenSidebarMenus,
} from "../../../../hooks/sidebar-menu.hooks";
import { NavigationRoute, ReviewerRoutes } from "./Sidebar.const";
import {
  SIDEBAR_ROLES,
  TSidebarMenuKey,
  TSidebarRole,
} from "./sidebarMenuVisibility";

/**
 * The reviewer level a sidebar list belongs to, or undefined for a list the
 * Admin cannot change. It goes by the list and not by the logged-in role,
 * because a System Admin outside admin mode is shown the Level 3 list.
 */
function reviewerRoleOfRoutes(routes: NavigationRoute[]) {
  return SIDEBAR_ROLES.find((role) => ReviewerRoutes[role] === routes);
}

/** The list without the entries that are switched off. */
function removeHiddenRoutes(
  routes: NavigationRoute[],
  hiddenKeys: TSidebarMenuKey[]
) {
  if (!hiddenKeys.length) return routes;
  return routes.filter(
    (route) =>
      !(
        route.type === "navigate" &&
        route.menuKey &&
        hiddenKeys.includes(route.menuKey)
      )
  );
}

function firstLink(routes: NavigationRoute[]) {
  for (const route of routes) {
    if (route.type === "navigate") return route.link;
  }
  return undefined;
}

/**
 * The sidebar list as the Admin has set it for that level. Any other list
 * (Admin, Accountant, applicant) comes back unchanged and asks the server
 * nothing.
 */
export function useVisibleSidebarRoutes(routes: NavigationRoute[]) {
  const role = reviewerRoleOfRoutes(routes);
  const { data: hidden } = useHiddenSidebarMenus({ enabled: !!role });
  return role ? removeHiddenRoutes(routes, hidden?.[role] ?? []) : routes;
}

/**
 * Where a reviewer lands after logging in: the first entry of the level's
 * sidebar that is still shown. That is the Dashboard, or User Approvals when
 * the Admin has switched the Dashboard off. When the switches cannot be read
 * the reviewer lands on the Dashboard as before.
 */
export async function resolveReviewerHome(role: TSidebarRole) {
  const routes = ReviewerRoutes[role];
  const dashboard = firstLink(routes) ?? "/";
  if (!branchFeatureFlags.ENABLE_SIDEBAR_MENU_CONFIG) return dashboard;

  try {
    const response = await fetchHiddenSidebarMenus();
    const hiddenKeys = response?.data?.data?.[role] ?? [];
    return firstLink(removeHiddenRoutes(routes, hiddenKeys)) ?? dashboard;
  } catch {
    return dashboard;
  }
}
